import { useCallback } from 'react';
import { useDockviewApi } from './dockview-context.js';
import { useAppStore, useActiveTabId } from './store.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Opens (or focuses) the Alerts panel in the active tab — the header bell's
// click target. Dockview-authoritative dedup: focus an existing Alerts panel
// rather than spawning a second. Opened floating; the user can dock it.
export function useOpenAlerts(): () => void {
  const activeTabId = useActiveTabId();
  const apiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);

  return useCallback(() => {
    if (!activeTabId) return;
    const api = apiRef.current;
    if (!api) return;
    const existing = api.panels.find((p) => p.api.component === 'alerts');
    if (existing) {
      existing.api.setActive();
      return;
    }
    const newId = addPanelToStore(activeTabId, { component: 'alerts', title: 'Alerts' });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newId,
      component: 'alerts' as PanelComponentKey,
      title: 'Alerts',
      params: { tabId: activeTabId, panelId: newId },
      floating: {
        position: { left: 140 + offset, top: 70 + offset },
        width: 460,
        height: 520,
      },
    });
  }, [activeTabId, apiRef, addPanelToStore]);
}
